/**
 * useAccountSummary — 2026-06-06 source-of-truth-full-ui.
 *
 * Single hook every Trading UI account surface reads from. Wraps
 * useViewerPortfolio() and exposes:
 *   - raw values (numbers/strings) for charting
 *   - formatted strings ready to render
 *   - a stable connected/disconnected discriminator
 *
 * Backend contract: GET /api/v1/user/portfolio (see
 * routes/user_portfolio.py:user_portfolio). When the viewer has a
 * connected Alpaca Paper row the payload includes:
 *   broker_connected, broker_provider, broker_mode,
 *   sync_status, last_sync_at, last_success_at, sync_error_code,
 *   buying_power, cash, equity, portfolio_value,
 *   daily_pl, daily_pl_pct, currency, account_number_masked,
 *   positions[], open_orders[], broker_connection_summary,
 *   demo_profile { first_name, last_name, email, demo_mode,
 *                  created_at_utc, organization, user_id_masked }
 *
 * Never fabricates numbers — missing values surface as the em-dash
 * formatter result.
 */
import useViewerPortfolio from './useViewerPortfolio';
import {
  EM_DASH,
  formatLastSyncedAt,
  formatPercent,
  formatPnL,
  formatUsd,
  syncStatusLabel,
  brokerConnectionLabel,
} from '../lib/accountFormatters';

export interface AccountSummary {
  loading: boolean;
  error: string | null;
  authenticated: boolean;

  brokerConnected: boolean;
  brokerProvider: string | null;
  brokerMode: string | null;
  source: string;
  syncStatus: 'not_connected' | 'synced' | 'sync_pending' | 'sync_failed' | string | null | undefined;

  // Raw values (string from API; null if unsynced).
  raw: {
    buyingPower: string | null;
    cash: string | null;
    equity: string | null;
    portfolioValue: string | null;
    dailyPl: string | null;
    dailyPlPct: string | null;
    currency: string;
    accountNumberMasked: string | null;
    lastSyncAt: string | null;
    lastSuccessAt: string | null;
  };

  // Formatted strings ready to render in JSX.
  display: {
    buyingPower: string;
    cash: string;
    equity: string;
    portfolioValue: string;
    dailyPl: string;
    dailyPlPct: string;
    accountNumberMasked: string;
    lastSyncedAt: string;
    syncStatus: string;
    brokerConnection: string;
  };

  positions: any[];
  openOrders: any[];

  demoProfile: {
    userIdMasked: string | null;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    fullName: string | null;
    organization: string | null;
    demoMode: boolean;
    createdAtUtc: string | null;
  };

  refetch: () => void;
}

const _NA = (v: string | null | undefined): string => (v && String(v).trim() ? v! : 'Not provided');

export default function useAccountSummary(): AccountSummary {
  const { data: viewer, loading, error, authenticated, refetch } = useViewerPortfolio();

  const v: any = viewer || {};
  const acct: any = v.account || {};
  const brokerConnected =
    Boolean(v.broker_connected) ||
    Boolean(v.broker_connection_connected) ||
    v.source === 'alpaca_paper_self_connected' ||
    v.source === 'connected_broker';

  const syncStatus = v.sync_status as AccountSummary['syncStatus'];

  const raw = {
    buyingPower: (acct.buying_power ?? v.buying_power ?? null) as string | null,
    cash: (acct.cash ?? v.cash ?? null) as string | null,
    equity: (acct.equity ?? v.equity ?? null) as string | null,
    portfolioValue: (acct.portfolio_value ?? v.portfolio_value ?? null) as string | null,
    dailyPl: (v.daily_pl ?? null) as string | null,
    dailyPlPct: (v.daily_pl_pct ?? null) as string | null,
    currency: (v.currency ?? acct.currency ?? 'USD') as string,
    accountNumberMasked: (acct.account_number_masked ?? v.account_number_masked ?? null) as string | null,
    lastSyncAt: (v.last_sync_at ?? null) as string | null,
    lastSuccessAt: (v.last_success_at ?? null) as string | null,
  };

  const isSynced = syncStatus === 'synced';
  const display = {
    buyingPower: isSynced ? formatUsd(raw.buyingPower) : EM_DASH,
    cash: isSynced ? formatUsd(raw.cash) : EM_DASH,
    equity: isSynced ? formatUsd(raw.equity) : EM_DASH,
    portfolioValue: isSynced ? formatUsd(raw.portfolioValue) : EM_DASH,
    dailyPl: isSynced ? formatPnL(raw.dailyPl) : EM_DASH,
    dailyPlPct: isSynced ? formatPercent(raw.dailyPlPct) : EM_DASH,
    accountNumberMasked: raw.accountNumberMasked || EM_DASH,
    lastSyncedAt: formatLastSyncedAt(raw.lastSuccessAt || raw.lastSyncAt),
    syncStatus: syncStatusLabel(syncStatus),
    brokerConnection: brokerConnectionLabel(brokerConnected, syncStatus),
  };

  const dp = v.demo_profile || {};
  const demoProfile = {
    userIdMasked: dp.user_id_masked ?? null,
    email: dp.email ?? v.email ?? null,
    firstName: dp.first_name ?? null,
    lastName: dp.last_name ?? null,
    fullName: dp.full_name ?? null,
    organization: dp.organization ?? null,
    demoMode: Boolean(dp.demo_mode),
    createdAtUtc: dp.created_at_utc ?? null,
  };

  return {
    loading,
    error,
    authenticated,
    brokerConnected,
    brokerProvider: (v.broker_provider ?? null) as string | null,
    brokerMode: (v.broker_mode ?? null) as string | null,
    source: (v.source ?? 'none') as string,
    syncStatus,
    raw,
    display,
    positions: Array.isArray(v.positions) ? v.positions : [],
    openOrders: Array.isArray(v.open_orders) ? v.open_orders : [],
    demoProfile,
    refetch: refetch ?? (() => undefined),
  };
}

/** Helper for "Not provided" fallback when rendering demo profile fields. */
export function naOrValue(v: string | null | undefined): string {
  return _NA(v);
}
